import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";

import sdk from "../../sdk";
import {
  BalanceExportTaskDataFragment,
  BalanceExportTaskStatus,
} from "../../graphql";

interface ExportState {
  task: BalanceExportTaskDataFragment | null;
  starting: boolean;
}

const POLL_INTERVAL_MS = 3000;

function isTerminal(status: BalanceExportTaskStatus | undefined) {
  return (
    status === BalanceExportTaskStatus.Done ||
    status === BalanceExportTaskStatus.Failed
  );
}

function useExportTask(withMnt: boolean) {
  const [state, setState] = useState<ExportState>({
    task: null,
    starting: false,
  });
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const pollOnce = useCallback(async (taskId: string) => {
    try {
      const { balanceExportTask } = await sdk.balanceExportTask({ taskId });
      setState((prev) => ({ ...prev, task: balanceExportTask }));
      if (
        balanceExportTask.status === BalanceExportTaskStatus.Failed &&
        balanceExportTask.error
      ) {
        message.warn(balanceExportTask.error, 4);
      }
      return balanceExportTask;
    } catch (err: any) {
      message.warn(err?.response?.errors?.[0]?.message ?? "Алдаа гарлаа.", 3);
      throw err;
    }
  }, []);

  const schedulePoll = useCallback(
    (taskId: string) => {
      clearTimer();
      pollTimer.current = setTimeout(async () => {
        try {
          const next = await pollOnce(taskId);
          if (!isTerminal(next.status)) {
            schedulePoll(taskId);
          }
        } catch {
          // Stop polling on transport errors; the user can retry from the UI.
        }
      }, POLL_INTERVAL_MS);
    },
    [clearTimer, pollOnce]
  );

  useEffect(() => clearTimer, [clearTimer]);

  const start = useCallback(async () => {
    setState({ task: null, starting: true });
    try {
      const { generateBalancesDetail } = await sdk.generateBalancesDetail({
        withMnt,
      });
      setState({ task: generateBalancesDetail, starting: false });
      if (!isTerminal(generateBalancesDetail.status)) {
        schedulePoll(generateBalancesDetail._id);
      }
    } catch (err: any) {
      setState({ task: null, starting: false });
      message.warn(err?.response?.errors?.[0]?.message ?? "Алдаа гарлаа.", 3);
    }
  }, [withMnt, schedulePoll]);

  const reset = useCallback(() => {
    clearTimer();
    setState({ task: null, starting: false });
  }, [clearTimer]);

  const inFlight =
    state.starting ||
    (!!state.task && !isTerminal(state.task.status));

  return {
    task: state.task,
    inFlight,
    start,
    reset,
  };
}

export const useBalanceDetail = () => {
  const usd = useExportTask(false);
  const mnt = useExportTask(true);

  return {
    // Plain USD export
    data: usd.task?.url ?? null,
    loading: usd.inFlight,
    status: usd.task?.status ?? null,
    generateBalancesDetail: usd.start,
    resetBalancesDetail: usd.reset,

    // Same export, but with MNT-valued stock columns
    mntData: mnt.task?.url ?? null,
    mntLoading: mnt.inFlight,
    mntStatus: mnt.task?.status ?? null,
    generateBalancesDetailMnt: mnt.start,
    resetBalancesDetailMnt: mnt.reset,
  };
};
